// src/Dashboard/admin/components/AudiencePicker.jsx
import { Search, X } from "lucide-react";

const GROUPS = [
  { value: "all_learners", label: "All learners" },
  { value: "active_learners", label: "Active learners" },
  { value: "inactive_learners", label: "Inactive learners" },
  { value: "graduates_by_course", label: "Graduates (by course)" },
  { value: "instructors", label: "Instructors" },
  { value: "admins", label: "Admins" },
];

function groupLabel(key) {
  const found = GROUPS.find((g) => g.value === key);
  return found?.label || key || "—";
}

export default function AudiencePicker({
  // mode
  audienceType = "scope", // scope | group | users
  setAudienceType,

  // group
  audienceKey,
  setAudienceKey,
  courseSlug,
  setCourseSlug,

  // users
  searchQ,
  setSearchQ,
  roleFilter = "learner",
  setRoleFilter,
  statusFilter = "all",
  setStatusFilter,
  searching = false,
  results = [],
  onSearch,
  selectedUsers = [],
  setSelectedUsers,
}) {
  const addUser = (u) => {
    const id = String(u?.id || "");
    if (!id) return;
    if (selectedUsers.some((x) => String(x.id) === id)) return;
    setSelectedUsers([...selectedUsers, u]);
  };

  const addAllResults = () => {
    const next = [...selectedUsers];
    (results || []).forEach((u) => {
      const id = String(u?.id || "");
      if (!id) return;
      if (next.some((x) => String(x.id) === id)) return;
      next.push(u);
    });
    setSelectedUsers(next);
  };

  const removeUser = (id) => {
    setSelectedUsers(selectedUsers.filter((x) => String(x.id) !== String(id)));
  };

  const clearUsers = () => setSelectedUsers([]);

  const setType = (nextType) => {
    // reset irrelevant fields to prevent stale payload data
    if (nextType !== "group") {
      setAudienceKey?.("");
      setCourseSlug?.("");
    }
    if (nextType !== "users") {
      setSearchQ?.("");
      setRoleFilter?.("learner");
      setStatusFilter?.("all");
      setSelectedUsers?.([]);
    }
    setAudienceType(nextType);
  };

  const showCourseSlug = audienceType === "group" && audienceKey === "graduates_by_course";

  const summary =
    audienceType === "scope"
      ? "Everyone in current scope"
      : audienceType === "group"
      ? `${groupLabel(audienceKey)}${showCourseSlug && courseSlug ? ` • ${courseSlug}` : ""}`
      : `Specific individuals (${selectedUsers.length})`;

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-950">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold">Audience</div>
          <div className="text-xs text-slate-500">
            Choose who receives this (inside the selected scope).
          </div>
          <div className="mt-2 inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
            {summary}
          </div>
        </div>

        <select
          value={audienceType}
          onChange={(e) => setType(e.target.value)}
          className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm shrink-0"
        >
          <option value="scope">Everyone in scope</option>
          <option value="group">A group in scope</option>
          <option value="users">Specific individuals</option>
        </select>
      </div>

      {audienceType === "scope" ? (
        <div className="text-sm text-slate-600 dark:text-slate-300">
          This will send to <span className="font-semibold">everyone</span> within the currently selected scope.
        </div>
      ) : null}

      {audienceType === "group" && (
        <div className="grid md:grid-cols-3 gap-3">
          <div className={showCourseSlug ? "md:col-span-2" : "md:col-span-3"}>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Group
            </label>
            <select
              value={audienceKey || ""}
              onChange={(e) => {
                const next = e.target.value;
                setAudienceKey(next);
                if (next !== "graduates_by_course") setCourseSlug("");
              }}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            >
              <option value="">Select group…</option>
              {GROUPS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
            <div className="mt-1 text-[11px] text-slate-500">
              Groups are evaluated <b>inside scope</b> (server enforces scope).
            </div>
          </div>

          {showCourseSlug && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Course slug
              </label>
              <input
                value={courseSlug || ""}
                onChange={(e) => setCourseSlug(e.target.value)}
                placeholder="discover"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
              <div className="mt-1 text-[11px] text-slate-500">
                Required for <b>Graduates (by course)</b>.
              </div>
            </div>
          )}
        </div>
      )}

      {audienceType === "users" && (
        <div className="space-y-3">
          {/* filters */}
          <div className="grid md:grid-cols-4 gap-2">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Search people
              </label>
              <div className="relative">
                <input
                  value={searchQ || ""}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="name / email / phone…"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              >
                <option value="learner">Learners</option>
                <option value="instructor">Instructors</option>
                <option value="admin">Admins</option>
                <option value="all">All</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={clearUsers}
              disabled={selectedUsers.length === 0}
              className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60"
            >
              Clear selected
            </button>
            <button
              type="button"
              onClick={onSearch}
              disabled={searching || !String(searchQ || "").trim()}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold disabled:opacity-60"
            >
              {searching ? "Searching…" : "Search"}
            </button>
          </div>

          {/* results */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-950 text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center justify-between">
              <span>Results</span>
              <button
                type="button"
                onClick={addAllResults}
                disabled={results.length === 0}
                className="text-[11px] px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-60"
              >
                Add all
              </button>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[240px] overflow-auto">
              {results.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">No results.</div>
              ) : (
                results.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => addUser(u)}
                    className="w-full text-left px-3 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  >
                    <div className="font-semibold text-sm">{u.full_name || u.fullName || "—"}</div>
                    <div className="text-xs text-slate-500">
                      {u.email || "—"} • {u.role || "—"}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* selected */}
          <div>
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Selected ({selectedUsers.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-2 px-2 py-1 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs"
                >
                  <span className="font-semibold">
                    {u.full_name || u.fullName || u.email || "User"}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeUser(u.id)}
                    className="p-0.5 hover:text-red-600"
                    aria-label="Remove"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
              {selectedUsers.length === 0 && (
                <span className="text-xs text-slate-500">None selected.</span>
              )}
            </div>
          </div>

          <div className="text-[11px] text-slate-500">
            Tip: This mode will send to the selected people <b>only if they are inside your scope</b> (server enforces scope).
          </div>
        </div>
      )}
    </div>
  );
}
